import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import type { Habit } from '../types';
import { showToast } from '../utils/toast';

type DbHabit = Database['public']['Tables']['habits']['Row'];
type DbHabitInsert = Database['public']['Tables']['habits']['Insert'];
type DbHabitUpdate = Database['public']['Tables']['habits']['Update'];
type DbHabitLog = Database['public']['Tables']['habit_logs']['Row'];

type DbHabitWithLogs = DbHabit & { habit_logs?: { log_date: string; status: string | null }[] };

const isValidUUID = (id: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
const safeUUID = (id?: string | null) => (id && isValidUUID(id) ? id : null);

const mapToHabit = (e: DbHabitWithLogs): Habit => ({
  id: e.id,
  name: e.title, // Mapping title (db) to name (frontend)
  frequency: (e.frequency || 'daily') as string,
  domainId: e.domain_id || '',
  // Get all completed logs and format them to YYYY-MM-DD
  completedDates:
    e.habit_logs?.filter((log) => log.status === 'completed')?.map((log) => log.log_date) || [],
});

export const habitService = {
  async getHabits(): Promise<Habit[]> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Vous devez être connecté.');

      // Fetch habits with their nested logs
      const { data, error } = await supabase
        .from('habits')
        .select('*, habit_logs(log_date, status)')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[habit.service] getHabits:', error.message);
        showToast('Erreur lors du chargement des habitudes', 'error');
        return [];
      }
      return (data || []).map(mapToHabit);
    } catch (err: any) {
      console.error('[habit.service] getHabits:', err.message);
      showToast('Erreur lors du chargement des habitudes', 'error');
      return [];
    }
  },

  async createHabit(habit: Partial<Habit>): Promise<Habit> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    const insertData: DbHabitInsert = {
      user_id: user.id,
      title: habit.name || 'Nouvelle habitude', // Convert name back to title
      description: null,
      frequency: (habit.frequency as Database['public']['Enums']['habit_frequency']) || 'daily',
      domain_id: safeUUID(habit.domainId),
    };

    const { data, error } = await supabase
      .from('habits')
      .insert(insertData)
      .select('*, habit_logs(log_date, status)')
      .single();

    if (error) {
      console.error('[habit.service] createHabit:', error.message);
      showToast("Impossible de créer l'habitude", 'error');
      if (error.code === '23503') {
        throw new Error(
          "Impossible d'enregistrer l'habitude : le domaine lié n'existe pas encore dans la base de données Supabase."
        );
      }
      throw error;
    }
    return mapToHabit(data);
  },

  async updateHabit(id: string, updates: Partial<Habit>): Promise<Habit> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    const updateData: DbHabitUpdate = {
      ...(updates.name !== undefined && { title: updates.name }),
      ...(updates.frequency !== undefined && {
        frequency: updates.frequency as Database['public']['Enums']['habit_frequency'],
      }),
      ...(updates.domainId !== undefined && { domain_id: safeUUID(updates.domainId) }),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('habits')
      .update(updateData)
      .eq('id', id)
      .select('*, habit_logs(log_date, status)')
      .single();

    if (error) {
      console.error('[habit.service] updateHabit:', error.message);
      showToast("Impossible de mettre à jour l'habitude", 'error');
      if (error.code === '23503') {
        throw new Error(
          "Impossible d'enregistrer l'habitude : le domaine lié n'existe pas encore dans la base de données Supabase."
        );
      }
      throw error;
    }
    return mapToHabit(data);
  },

  async deleteHabit(id: string): Promise<void> {
    const { error } = await supabase.from('habits').delete().eq('id', id);

    if (error) {
      console.error('[habit.service] deleteHabit:', error.message);
      showToast("Impossible de supprimer l'habitude", 'error');
      throw error;
    }
  },

  // Toggle log function
  async toggleHabitLog(
    habitId: string,
    date: string,
    isCurrentlyCompleted: boolean
  ): Promise<boolean> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    if (isCurrentlyCompleted) {
      // It's completed, we need to remove or change the log status
      const { error } = await supabase
        .from('habit_logs')
        .delete()
        .eq('habit_id', habitId)
        .eq('log_date', date)
        .eq('user_id', user.id);

      if (error) {
        console.error('[habit.service] toggleHabitLog (delete):', error.message);
        showToast('Impossible de mettre à jour l’habitude', 'error');
        throw error;
      }
      return false; // Now false
    } else {
      // It's not completed, we insert a completed log
      const { error } = await supabase.from('habit_logs').upsert(
        {
          habit_id: habitId,
          log_date: date,
          user_id: user.id,
          status: 'completed',
        },
        {
          onConflict: 'habit_id, log_date', // assuming composite unique constraint, or standard upsert
        }
      );

      if (error) {
        console.error('[habit.service] toggleHabitLog (upsert):', error.message);
        showToast('Impossible de mettre à jour l’habitude', 'error');
        throw error;
      }
      return true; // Now true
    }
  },
};
